import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Buildings, CheckCircle, Clock, Warning, MapPin, Camera, ArrowLeft, SignOut, ChatCircleText, CalendarCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { RESIDENT_TOKEN_KEY } from "./TenantReportPublicPage";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS = {
  received: ["Received", "#2563EB"], open: ["Received — emergency dispatch", "#dc2626"], acknowledged: ["Acknowledged by team", "#d97706"],
  pending: ["Awaiting scheduling", "#d97706"], scheduled: ["Scheduled", "#2563EB"], in_progress: ["In progress", "#7c3aed"],
  submitted: ["Logged with our team", "#2563EB"], completed: ["Completed", "#059669"], cancelled: ["Cancelled", "#64748b"], closed: ["Closed", "#64748b"],
};
const sevColor = (s) => s === "critical" ? "#dc2626" : s === "urgent" ? "#d97706" : "#2563EB";
const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
const slotLabel = (s) => s ? `${new Date(s.date).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })} ${s.period} (${s.period === "AM" ? "8am–12pm" : "12pm–5pm"})` : "";

function api(token) {
  return axios.create({ baseURL: `${BACKEND}/api`, headers: token ? { "X-Resident-Token": token } : {} });
}

function Shell({ children, onSignOut, email }) {
  return (
    <div className="min-h-screen bg-background py-6" data-testid="resident-portal-page">
      <div className="max-w-2xl mx-auto px-4 space-y-5">
        <div className="flex items-start justify-between gap-3 pt-2">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md flex items-center justify-center bg-primary"><Buildings className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">My maintenance reports</h1>
              {email && <p className="text-xs text-slate-500" data-testid="resident-email">{email}</p>}
            </div>
          </div>
          {onSignOut && <button type="button" onClick={onSignOut} className="text-xs text-slate-500 inline-flex items-center gap-1 hover:underline" data-testid="resident-signout"><SignOut className="h-3.5 w-3.5" /> Sign out</button>}
        </div>
        {children}
        <p className="text-[11px] text-center text-slate-400 pb-4">Powered by Facilities Management Platform</p>
      </div>
    </div>
  );
}

function SignIn({ onSignedIn }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("email");
  const [busy, setBusy] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api().post("/tenant-report/auth/request-code", { email }); setStage("code"); toast.success("If we have reports for that email, a code is on its way"); }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't send a code"); }
    finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api().post("/tenant-report/auth/verify", { email, code }); localStorage.setItem(RESIDENT_TOKEN_KEY, data.token); onSignedIn(data); }
    catch (err) { toast.error(err.response?.data?.detail || "That code didn't work"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="resident-signin">
      <p className="text-sm text-slate-600 dark:text-slate-300">Enter the email you used when reporting. We'll send a 6-digit code — no password needed.</p>
      {stage === "email" ? (
        <form onSubmit={request} className="space-y-3">
          <div><Label>Email</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="mt-1" data-testid="resident-email-input" /></div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="resident-request-code">{busy ? "Sending…" : "Email me a code"}</Button>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-3">
          <p className="text-xs text-slate-500">Code sent to <strong>{email}</strong>. It expires in 10 minutes.</p>
          <div><Label>6-digit code</Label><Input inputMode="numeric" pattern="[0-9]*" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} required className="mt-1 text-center text-2xl tracking-[0.5em] font-mono" data-testid="resident-code-input" /></div>
          <Button type="submit" className="w-full" disabled={busy || code.length !== 6} data-testid="resident-verify-code">{busy ? "Checking…" : "Sign in"}</Button>
          <button type="button" onClick={() => setStage("email")} className="text-xs text-slate-500 underline w-full text-center">Use a different email</button>
        </form>
      )}
    </div>
  );
}

function ReportCard({ r, onOpen }) {
  const [label, color] = STATUS[r.live_status] || [r.live_status, "#64748b"];
  return (
    <button type="button" onClick={() => onOpen(r)} className="w-full text-left bg-card border border-border rounded-xl p-4 hover:border-primary/50 transition" data-testid={`resident-report-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold truncate">{r.title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{r.reference} · {r.category} · {new Date(r.submitted_at).toLocaleDateString("en-GB")}</p>
        </div>
        <span className="text-[10px] px-2 py-1 rounded-full font-medium uppercase tracking-wider shrink-0" style={{ backgroundColor: `${sevColor(r.severity)}20`, color: sevColor(r.severity) }}>{(r.severity || "").replace("_", " ")}</span>
      </div>
      <div className="flex items-center gap-2 mt-3 text-sm" style={{ color }}>
        {r.live_status === "completed" ? <CheckCircle className="h-4 w-4" weight="fill" /> : <Clock className="h-4 w-4" />}
        <span className="font-medium" data-testid={`resident-report-status-${r.id}`}>{label}</span>
        {r.confirmed_slot && <span className="ml-auto text-xs text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1"><CalendarCheck className="h-3.5 w-3.5" /> {slotLabel(r.confirmed_slot)}</span>}
      </div>
    </button>
  );
}

function ReportDetail({ token, report, onBack }) {
  const [r, setR] = useState(report);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(async () => { try { const { data } = await api(token).get(`/tenant-report/me/reports/${report.id}`); setR(data); } catch { /* keep */ } }, [token, report.id]);
  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setBusy(true);
    try { await api(token).post(`/tenant-report/me/reports/${r.id}/comments`, { message: msg }); setMsg(""); toast.success("Sent to our team"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't send"); }
    finally { setBusy(false); }
  };
  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append("files", f));
    setBusy(true);
    try { await api(token).post(`/tenant-report/me/reports/${r.id}/photos`, fd); toast.success(`${files.length} file(s) added`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const [label, color] = STATUS[r.live_status] || [r.live_status, "#64748b"];
  const stops = r.triage?.stops || [];
  return (
    <div className="space-y-4" data-testid="resident-report-detail">
      <button type="button" onClick={onBack} className="text-sm text-slate-500 inline-flex items-center gap-1 hover:underline" data-testid="resident-back"><ArrowLeft className="h-4 w-4" /> All reports</button>
      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div><p className="text-xs text-slate-500">{r.reference}</p><p className="font-semibold text-lg">{r.title}</p><p className="text-xs text-slate-500">{r.category}</p></div>
          <span className="text-[10px] px-2 py-1 rounded-full font-medium uppercase tracking-wider shrink-0" style={{ backgroundColor: `${sevColor(r.severity)}20`, color: sevColor(r.severity) }}>{(r.severity || "").replace("_", " ")}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}20`, color }}>{r.live_status === "completed" ? <CheckCircle className="h-5 w-5" weight="fill" /> : <Clock className="h-5 w-5" />}</div>
          <div><p className="font-semibold" style={{ color }} data-testid="resident-detail-status">{label}</p><p className="text-xs text-slate-500">Last update {fmt(r.last_update)}</p></div>
        </div>
        {r.confirmed_slot ? (
          <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-3 text-sm text-emerald-800 dark:text-emerald-200 flex items-center gap-2" data-testid="resident-appointment"><CalendarCheck className="h-4 w-4" weight="fill" /> Appointment confirmed: <strong>{slotLabel(r.confirmed_slot)}</strong></div>
        ) : r.preferred_slots?.length > 0 ? (
          <p className="text-xs text-slate-500" data-testid="resident-preferred">You offered {r.preferred_slots.length} slot{r.preferred_slots.length === 1 ? "" : "s"}: {r.preferred_slots.map(s => `${new Date(s.date).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })} ${s.period}`).join(", ")}. We'll confirm one with you.</p>
        ) : null}
        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{r.description}</p>
        <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {[r.address?.street_address, r.address?.town_city, r.address?.postcode].filter(Boolean).join(", ") || "—"}</p>
        {stops.length > 0 && <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 p-3 text-xs text-red-800 dark:text-red-200 flex gap-2"><Warning className="h-4 w-4 shrink-0" weight="fill" /><span>{stops.join(" ")}</span></div>}
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span data-testid="resident-attachments-count">{(r.attachments || []).length} photo/video attachment{(r.attachments || []).length === 1 ? "" : "s"}</span>
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={addPhotos} data-testid="resident-photo-input" />
          <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="resident-add-photos"><Camera className="h-4 w-4 mr-1" /> Add photos</Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2"><ChatCircleText className="h-4 w-4" /> Messages with our team</h2>
        {(r.comments || []).length === 0 && <p className="text-xs text-slate-500">No messages yet. Let us know if anything changes or you need to rearrange.</p>}
        <div className="space-y-2">
          {(r.comments || []).map(c => (
            <div key={c.id} className={`rounded-lg p-3 text-sm max-w-[90%] ${c.by === "resident" ? "ml-auto bg-primary/10" : "bg-muted/50"}`} data-testid={`resident-comment-${c.id}`}>
              <p className="whitespace-pre-wrap">{c.message}</p>
              <p className="text-[10px] text-slate-500 mt-1">{c.by === "resident" ? "You" : c.name || "Our team"} · {fmt(c.at)}</p>
            </div>
          ))}
        </div>
        <form onSubmit={send} className="space-y-2">
          <Textarea rows={2} value={msg} onChange={e => setMsg(e.target.value)} placeholder="Add an update, access instructions or a question…" data-testid="resident-comment-input" />
          <Button type="submit" size="sm" disabled={busy || !msg.trim()} data-testid="resident-comment-send">Send</Button>
        </form>
      </div>

      {(r.timeline || []).length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-3">
          <h2 className="text-sm font-semibold">Activity</h2>
          {r.timeline.slice().reverse().map((t, i) => (
            <div key={i} className="text-xs border-l-2 border-border pl-3 py-0.5"><p className="text-slate-700 dark:text-slate-300">{t.description}</p><p className="text-[10px] text-slate-400 mt-0.5">{fmt(t.created_at)}</p></div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ResidentPortalPage() {
  const [token, setToken] = useState(() => localStorage.getItem(RESIDENT_TOKEN_KEY) || "");
  const [me, setMe] = useState(null);
  const [reports, setReports] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [m, r] = await Promise.all([api(token).get("/tenant-report/me"), api(token).get("/tenant-report/me/reports")]);
      setMe(m.data); setReports(r.data.reports);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem(RESIDENT_TOKEN_KEY); setToken(""); }
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const signOut = async () => { try { await api(token).post("/tenant-report/auth/logout"); } catch { /* ignore */ } localStorage.removeItem(RESIDENT_TOKEN_KEY); setToken(""); setMe(null); setReports(null); setOpen(null); };

  if (!token) return <Shell><SignIn onSignedIn={(d) => { setToken(d.token); }} /></Shell>;
  if (open) return <Shell email={me?.email} onSignOut={signOut}><ReportDetail token={token} report={open} onBack={() => { setOpen(null); load(); }} /></Shell>;
  return (
    <Shell email={me?.email} onSignOut={signOut}>
      {reports === null ? (
        <div className="flex justify-center py-10"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : reports.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center" data-testid="resident-empty">
          <p className="font-semibold">No reports yet for this email</p>
          <p className="text-sm text-slate-500 mt-1">Use the reporting link or QR code from your property manager to report an issue.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="resident-report-list">
          {reports.map(r => <ReportCard key={r.id} r={r} onOpen={setOpen} />)}
        </div>
      )}
      {me?.last_form_token && <Link to={`/report-issue/${me.last_form_token}`} className="block"><Button variant="outline" className="w-full" data-testid="resident-report-new">Report another issue</Button></Link>}
    </Shell>
  );
}
